/**
 * proseFormatter — reorganiza texto corrido excessivo em blocos curtos.
 *
 * Funções puras (sem JSX, sem estado). Usadas em dois pontos:
 *   - AthenaPresentationEngine (Caso 1: resposta sem card, vira markdown)
 *   - ProseText (Casos 2/3: texto dentro/ao redor de cards premium)
 *
 * Regra de ouro: texto curto/conversacional nunca é tocado. Nenhum
 * significado, fato ou palavra é removido — só a estrutura visual muda.
 */

const DEFAULT_THRESHOLD = 300;
const MIN_PARAGRAPHS_UNSTRUCTURED = 3;

const HEADING_RE = /^#{1,6}\s+/;
const LIST_LINE_RE = /^\s*([-*•]|\d+[.)])\s+/;
const TABLE_LINE_RE = /^\s*\|.*\|\s*$/;
const LABEL_RE = /^\*\*([^*\n]{2,40})\*\*[:\s]+(.+)$/s;

// Conectores que indicam etapas escritas como prosa ("depois,", "em seguida,"...)
const SEQUENCE_CONNECTORS_RE = /\b(em seguida|depois disso|logo (em seguida|depois)|por (fim|último)|primeiro|segundo|terceiro|por último)\b[,:]/gi;
// Mesma regra, sem /g — segura para uso com .test() em loop (regex global mantém lastIndex).
const SEQUENCE_CONNECTOR_STARTS_RE = /^\s*(em seguida|depois disso|logo (em seguida|depois)|por (fim|último)|primeiro|segundo|terceiro|por último)\b[,:]/i;

// Enumeração inline: "1) texto 2) texto" ou "1. texto 2. texto" dentro do mesmo parágrafo
const INLINE_ENUM_RE = /(?:^|\s)(\d{1,2})[.)]\s+\S/g;

function isFence(line) {
  return /^\s*```/.test(line);
}

/** Divide o texto em segmentos, protegendo blocos de código ```...``` intactos. */
function splitProtectingFences(text) {
  const lines = text.split('\n');
  const segments = [];
  let buf = [];
  let inFence = false;

  for (const line of lines) {
    if (isFence(line)) {
      if (!inFence) {
        if (buf.length) segments.push({ type: 'prose', text: buf.join('\n') });
        buf = [line];
        inFence = true;
      } else {
        buf.push(line);
        segments.push({ type: 'code', text: buf.join('\n') });
        buf = [];
        inFence = false;
      }
      continue;
    }
    buf.push(line);
  }
  if (buf.length) segments.push({ type: inFence ? 'code' : 'prose', text: buf.join('\n') });
  return segments;
}

function splitParagraphs(text) {
  return text.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
}

function paragraphLines(paragraph) {
  return paragraph.split('\n');
}

function isAlreadyStructuredLine(line) {
  return HEADING_RE.test(line) || LIST_LINE_RE.test(line) || TABLE_LINE_RE.test(line);
}

function paragraphIsStructured(paragraph) {
  return paragraphLines(paragraph).some(isAlreadyStructuredLine);
}

/** Quebra um texto em frases (mantém a pontuação de fim de frase). */
function splitIntoSentenceUnits(text) {
  const sentences = (text.match(/[^.!?]+[.!?]+(\s+|$)|[^.!?]+$/g) || [text])
    .map(s => s.trim())
    .filter(Boolean);
  return sentences.length ? sentences : [text];
}

/**
 * Agrupa unidades (frases, ou cláusulas por vírgula) em blocos de até `threshold`
 * caracteres, sem nunca cortar uma unidade no meio.
 */
function groupUnitsByThreshold(units, threshold, sep = ' ') {
  const chunks = [];
  let current = '';
  for (const u of units) {
    const candidate = current ? current + sep + u : u;
    if (current && candidate.trim().length > threshold) {
      chunks.push(current.trim());
      current = u;
    } else {
      current = candidate;
    }
  }
  if (current.trim()) chunks.push(current.trim());
  return chunks;
}

/**
 * Quebra um parágrafo longo em sub-parágrafos por fronteira de frase, sem cortar
 * frases nem remover palavras. Quando o parágrafo é uma única frase-corrida (sem
 * pontuação de fim de frase no meio), cai para fronteiras de vírgula/cláusula —
 * ainda assim nunca corta uma palavra.
 */
function splitLongParagraphBySentence(paragraph, threshold) {
  const sentences = splitIntoSentenceUnits(paragraph);
  let chunks = groupUnitsByThreshold(sentences, threshold);

  if (chunks.length <= 1 && paragraph.trim().length > threshold) {
    const clauses = paragraph.split(/,\s+/).map(c => c.trim()).filter(Boolean);
    if (clauses.length > 1) {
      // Reanexa a vírgula em todas as cláusulas menos a última, pra não perder pontuação.
      const withCommas = clauses.map((c, i) => (i < clauses.length - 1 ? c + ',' : c));
      chunks = groupUnitsByThreshold(withCommas, threshold);
    }
  }

  return chunks.length ? chunks : [paragraph];
}

function countInlineEnumMatches(paragraph) {
  const matches = paragraph.match(INLINE_ENUM_RE);
  return matches ? matches.length : 0;
}

function countSequenceConnectors(paragraph) {
  const matches = paragraph.match(SEQUENCE_CONNECTORS_RE);
  return matches ? matches.length : 0;
}

/** Converte um parágrafo com enumeração inline ("1) x 2) y") em itens de lista. */
function extractInlineEnumItems(paragraph) {
  const parts = paragraph.split(/(?:^|\s)\d{1,2}[.)]\s+/).map(s => s.trim()).filter(Boolean);
  return parts;
}

/**
 * true se `text` conta como "texto corrido excessivo" pelos critérios definidos.
 * Texto curto/conversacional sempre retorna false.
 */
export function isExcessiveProse(text, opts = {}) {
  const threshold = opts.threshold ?? DEFAULT_THRESHOLD;
  if (!text || typeof text !== 'string') return false;
  const trimmed = text.trim();
  if (!trimmed) return false;

  const segments = splitProtectingFences(trimmed);
  const proseText = segments.filter(s => s.type === 'prose').map(s => s.text).join('\n\n');
  if (!proseText.trim()) return false;

  const paragraphs = splitParagraphs(proseText);
  if (paragraphs.length === 0) return false;

  // (a) algum parágrafo isolado maior que o limite
  if (paragraphs.some(p => !paragraphIsStructured(p) && p.length > threshold)) return true;

  // (b) 3+ parágrafos, nenhum estruturado (sem heading/lista/tabela)
  const unstructured = paragraphs.filter(p => !paragraphIsStructured(p));
  if (paragraphs.length >= MIN_PARAGRAPHS_UNSTRUCTURED && unstructured.length === paragraphs.length) {
    return true;
  }

  // (c) enumeração escrita em frases consecutivas / inline
  if (paragraphs.some(p => !paragraphIsStructured(p) && countInlineEnumMatches(p) >= 2)) return true;

  // (d) sequência de etapas escrita como prosa (conectores sequenciais repetidos)
  if (paragraphs.some(p => !paragraphIsStructured(p) && countSequenceConnectors(p) >= 2)) return true;

  // (e) 2+ parágrafos cujo total combinado excede o limite — cobre respostas
  // com vários assuntos (introdução + sugestão + motivo + aplicação) escritas
  // em modo conversa, onde cada parágrafo isolado pode ficar abaixo do limite
  // mas o conjunto ainda é longo demais para permanecer como texto corrido.
  if (paragraphs.length >= 2 && !paragraphs.every(paragraphIsStructured)) {
    const totalLength = paragraphs.reduce((sum, p) => sum + p.length, 0);
    if (totalLength > threshold) return true;
  }

  return false;
}

/**
 * Quebra `text` em blocos estruturais: {type:'code'|'heading'|'paragraph'|'list', ...}
 * Nunca remove conteúdo — só reorganiza.
 */
export function splitProseIntoBlocks(text, opts = {}) {
  const threshold = opts.threshold ?? DEFAULT_THRESHOLD;
  if (!text || typeof text !== 'string') return [];

  const segments = splitProtectingFences(text.trim());
  const blocks = [];

  for (const seg of segments) {
    if (seg.type === 'code') {
      blocks.push({ type: 'code', text: seg.text });
      continue;
    }

    const paragraphs = splitParagraphs(seg.text);
    for (const paragraph of paragraphs) {
      if (paragraphIsStructured(paragraph)) {
        // já tem heading/lista/tabela — preserva como veio
        blocks.push({ type: 'raw', text: paragraph });
        continue;
      }

      // Rótulo em negrito no início ("**Rótulo:** resto") → heading + parágrafo
      const labelMatch = paragraph.match(LABEL_RE);
      let working = paragraph;
      if (labelMatch) {
        blocks.push({ type: 'heading', text: labelMatch[1].trim().replace(/:\s*$/, '') });
        working = labelMatch[2].trim();
        if (!working) continue;
      }

      if (countInlineEnumMatches(working) >= 2) {
        const items = extractInlineEnumItems(working);
        if (items.length >= 2) {
          blocks.push({ type: 'list', items });
          continue;
        }
      }

      if (countSequenceConnectors(working) >= 2) {
        const sentences = splitIntoSentenceUnits(working);
        const isConnectorStart = s => SEQUENCE_CONNECTOR_STARTS_RE.test(s);
        const firstIdx = sentences.findIndex(isConnectorStart);
        const lastIdx = sentences.reduce((acc, s, i) => (isConnectorStart(s) ? i : acc), -1);

        if (firstIdx !== -1 && lastIdx > firstIdx) {
          const before = sentences.slice(0, firstIdx).join(' ').trim();
          const items = sentences.slice(firstIdx, lastIdx + 1);
          const after = sentences.slice(lastIdx + 1).join(' ').trim();

          if (before) splitLongParagraphBySentence(before, threshold).forEach(c => blocks.push({ type: 'paragraph', text: c }));
          blocks.push({ type: 'list', items });
          if (after) splitLongParagraphBySentence(after, threshold).forEach(c => blocks.push({ type: 'paragraph', text: c }));
          continue;
        }
      }

      if (working.length > threshold) {
        const chunks = splitLongParagraphBySentence(working, threshold);
        chunks.forEach(c => blocks.push({ type: 'paragraph', text: c }));
        continue;
      }

      blocks.push({ type: 'paragraph', text: working });
    }
  }

  return blocks;
}

/**
 * Quebra uma string em tokens { bold:boolean, text:string } respeitando marcação
 * **negrito** já presente no texto original. Usado por ProseText para não perder
 * negrito moderado que já existia nos componentes antes desta mudança.
 */
export function tokenizeInlineBold(text) {
  if (!text) return [];
  const parts = String(text).split(/(\*\*[^*\n]+\*\*)/g);
  return parts.filter(Boolean).map(part => (
    part.startsWith('**') && part.endsWith('**')
      ? { bold: true, text: part.slice(2, -2) }
      : { bold: false, text: part }
  ));
}

/**
 * Serializa blocos de volta para markdown (Caso 1 — AthenaPresentationEngine).
 * Se o texto não for excessivo, retorna o texto original inalterado.
 */
export function formatProseMarkdown(text, opts = {}) {
  if (!text || typeof text !== 'string') return text;
  if (!isExcessiveProse(text, opts)) return text;

  const blocks = splitProseIntoBlocks(text, opts);
  const parts = [];
  for (const b of blocks) {
    if (b.type === 'code') parts.push(b.text);
    else if (b.type === 'raw') parts.push(b.text);
    else if (b.type === 'heading') parts.push(`### ${b.text}`);
    else if (b.type === 'list') parts.push(b.items.map(i => `- ${i}`).join('\n'));
    else parts.push(b.text);
  }
  return parts.join('\n\n');
}
